/**
 * Snap engine (docs/bim-plan/05 §3) — pure geometry, designed ONCE for both editing
 * surfaces. Priority: node > edge > ortho. Tolerances arrive in FEET (the caller
 * converts a screen-px radius via the surface's worldPerPixel, so snapping feels
 * identical at any zoom on any surface). Ortho preserves the shipped Phase-1 behavior
 * exactly: 45° increments engaged within ~7°, Shift = free.
 */
import type { ElementId, NodeId, Path2D, Pt2 } from './types'
import { listGrids, listSiteRecords, resolveAllElements, resolveElement, type ModelDoc } from './doc'
import { flattenSiteRing, siteEnvelope } from './site'
import { polylinePointAt, offsetAllEdges, offsetPolyline } from './pathOps'
import {
  bulgeToArc, arcQuadrants, arcPointAtAngle, angleOnArc, projectToArc,
  lineCircleIntersect, flattenPathPts, type EdgeArc,
} from './arc'
import { centerlineOffsetFt } from './locationLine'
import { pathCategories, hostedCategories } from './registry'
import { fixtureBodyDims } from './categories/fixture/fixtureKinds'
import { RUG_KINDS, SURFACE_SITTING_OBJECT_KINDS, TERRAIN_HOSTED_OBJECT_KINDS } from './categories/object/objectKinds'
import type { ResolvedElement } from './params'
import { awningFootprintSU, type AwningSpec } from './awning'
import { nearCandidates } from './snapIndex'
import { orthoMode, orthoConstrain } from './ortho'
import { quantFt, quantPt } from './resolution'

export interface SnapNode { pt: Pt2; elementId: ElementId; nodeId: NodeId }
export interface SnapSegment { a: Pt2; b: Pt2; elementId: ElementId }
/** CURVES round: a bulge edge as a snap candidate — the arc body/midpoint rungs and
 *  the guide×arc intersections resolve against this in CLOSED FORM (never a sampled
 *  polyline — Matt's r8-family performance mandate for the guide math). `fromNodeId`
 *  identifies the STORED path edge (main-path arcs only — derived face arcs carry
 *  none), which is what the radius/arc-length dimension pick captures as its edgeRef. */
export interface SnapArc extends EdgeArc { elementId: ElementId; fromNodeId?: NodeId }
export interface SnapCandidates { nodes: SnapNode[]; segments: SnapSegment[]; arcs?: SnapArc[] }

export interface SnapResult {
  pt: Pt2
  kind: 'node' | 'midpoint' | 'edge' | 'axis' | 'ortho' | 'none'
  target?: { elementId: ElementId; nodeId?: NodeId }
  /** kind 'axis': the matched constraint line(s) — TWO when the snap landed on an
   *  axis intersection. Callers draw temporary guide lines from each axis origin. */
  axes?: SnapAxis[]
  /** PERPENDICULAR SNAPS round (2026-08-16): true when this result is the foot of
   *  the perpendicular from the gesture's reference point onto the matched
   *  segment/arc. Rides kind 'edge' (mechanically it IS an on-edge landing — every
   *  marker/lock/typed-distance consumer already treats it right); the flag exists
   *  for labeling (status bar says "perpendicular"). */
  perp?: boolean
  /** GUIDE/SNAP HEALTH round: the exact segment a perpendicular foot was measured
   *  against, so the surfaces can HIGHLIGHT it (guideDecoration passes it through as
   *  the marker's refLine). Near-parallel lines stack in a real model (a wall's own
   *  faces, the DXF it was traced over) — showing WHICH line the PER is true to is
   *  what lets the user trust the snap before clicking. Segments only (arc feet skip
   *  it — the radial read is unambiguous). */
  perpEdge?: [Pt2, Pt2]
}

/** A directional constraint line (2.6 polish): end-node drags snap to the wall's own
 *  extension line; edge drags snap perpendicular/parallel to the edge. */
export interface SnapAxis { origin: Pt2; dir: Pt2 }

const ORTHO_STEP = Math.PI / 4
const ORTHO_WITHIN = (7 * Math.PI) / 180

function pathOf(el: { params: Record<string, unknown> }): Path2D | null {
  const v = (el.params.path ?? el.params.boundary) as Path2D | null
  return v && Array.isArray(v.nodes) && v.nodes.length >= 2 ? v : null
}

/**
 * Gather snappable geometry on a level. Every path/boundary category contributes its
 * nodes + edges; hosted components (door/window/casework) contribute their along-wall
 * EXTENT — the two jamb/edge points + the center midpoint + the span segment — so their
 * edges and midpoints snap too (Matt r4 item 2). `exclude`: with a nodeId, that node is
 * dropped and the dragged element's own SEGMENTS are dropped (its sibling nodes stay, so
 * an end can snap onto the other end to close a loop); with only an elementId, the whole
 * element is skipped (edge/translate/hosted-move drags don't snap to themselves).
 *
 * `levelId: null` gathers EVERY level (3D CROSS-LEVEL SNAP round — the 3D viewport's
 * drags align against elements on any story; 2D plans stay level-scoped).
 */
export function collectSnapCandidates(
  model: ModelDoc,
  levelId: string | null,
  exclude?: { elementId?: ElementId; nodeId?: NodeId },
): SnapCandidates {
  if (!profiling()) return collectSnapCandidatesImpl(model, levelId, exclude)
  const t0 = now()
  const res = collectSnapCandidatesImpl(model, levelId, exclude)
  perfMark('collectSnapCandidates', now() - t0, {
    nodes: res.nodes.length, segments: res.segments.length, arcs: res.arcs?.length || 0,
  })
  return res
}

function collectSnapCandidatesImpl(
  model: ModelDoc,
  levelId: string | null,
  exclude?: { elementId?: ElementId; nodeId?: NodeId },
): SnapCandidates {
  const nodes: SnapNode[] = []
  const segments: SnapSegment[] = []
  const arcs: SnapArc[] = []
  const wholeExclude = exclude?.elementId && !exclude?.nodeId
  // CURVES round: a path's edges contribute — straight edges as segments, bulge edges
  // as SnapArcs plus their CENTER + QUADRANT points (synthetic ':'-carrying ids, the
  // never-capture-into-a-witness convention; direct precedent: columnProfileRing's
  // quadrants). The chord of an arc edge is deliberately NOT a candidate — it isn't
  // real geometry.
  const pushPathEdges = (path: Path2D, elementId: ElementId, idPrefix: string, storedEdges = false): void => {
    const count = path.closed ? path.nodes.length : path.nodes.length - 1
    for (let i = 0; i < count; i++) {
      const a = path.nodes[i].pt, b = path.nodes[(i + 1) % path.nodes.length].pt
      const arc = bulgeToArc(a, b, path.nodes[i].bulge)
      if (!arc) {
        segments.push({ a: [a[0], a[1]], b: [b[0], b[1]], elementId })
        continue
      }
      arcs.push({ ...arc, elementId, ...(storedEdges ? { fromNodeId: path.nodes[i].id } : {}) })
      nodes.push({ pt: [arc.c[0], arc.c[1]], elementId, nodeId: `${idPrefix}:arcC${i}` as NodeId })
      arcQuadrants(arc).forEach((q, k) => {
        nodes.push({ pt: q, elementId, nodeId: `${idPrefix}:arcQ${i}-${k}` as NodeId })
      })
    }
  }
  // P6-clean: derived from the registry (pathParamKey / hostable declarations) —
  // new categories snap without touching this file
  const PATH_CATS = pathCategories()
  const HOSTED_CATS = hostedCategories()
  for (const el of resolveAllElements(model)) {
    if (levelId !== null && el.levelId !== levelId) continue
    if (wholeExclude && el.id === exclude!.elementId) continue

    if (PATH_CATS.has(el.category)) {
      const path = pathOf(el)
      if (!path) continue
      for (const n of path.nodes) {
        if (exclude?.nodeId && n.id === exclude.nodeId) continue
        nodes.push({ pt: [n.pt[0], n.pt[1]], elementId: el.id, nodeId: n.id })
      }
      if (exclude?.elementId && el.id === exclude.elementId) continue
      pushPathEdges(path, el.id, el.id, true)
      // wall: expose EVERY assembly layer face (not just the stored location line) so
      // other elements can snap to a specific face — e.g. casework against the wall's
      // interior finish face (Matt r6 item C). Mirrors bim-geometry's wallCoreLines
      // (generate2d.ts) offset math, reimplemented here since bim-kernel can't import
      // bim-geometry (check-boundaries.mjs) — keep the two in sync if either changes.
      if (el.category === 'wall' && el.assembly && el.assemblyThicknessFt > 0) {
        const assembly = el.assembly
        const t = el.assemblyThicknessFt
        const s0 = path.nodes[0].pt, sL = path.nodes[path.nodes.length - 1].pt
        const isLoop = !path.closed && path.nodes.length >= 4 &&
          Math.hypot(s0[0] - sL[0], s0[1] - sL[1]) < 1e-6
        const base: Path2D = isLoop ? { closed: true, nodes: path.nodes.slice(0, -1) } : path
        const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, assembly)
        const centerline = delta === 0 ? base : offsetPolyline(base, delta)
        if (centerline) {
          const sign = el.params.flipped === true ? -1 : 1
          const breaks = [0]
          let cum = 0
          for (const layer of assembly.layers) { cum += layer.thicknessFt; breaks.push(cum) }
          for (let bi = 0; bi < breaks.length; bi++) {
            const off = sign * (t / 2 - breaks[bi])
            const moved = Math.abs(off) < 1e-9 ? centerline : offsetAllEdges(centerline, off)
            if (!moved) continue
            moved.nodes.forEach((n, i) => {
              nodes.push({ pt: [n.pt[0], n.pt[1]], elementId: el.id, nodeId: `${el.id}:face${bi}n${i}` as NodeId })
            })
            // CURVES round: face edges route through the same segment-or-arc push —
            // a curved wall's faces snap as true concentric arcs
            pushPathEdges(moved, el.id, `${el.id}:face${bi}`)
          }
        }
      }
      // stair/ramp/beam: the run has WIDTH — expose its side-edge corners + edges so you
      // can snap to the actual body outline, not just the run centerline (Matt r5;
      // beams joined in the STRUCTURE round — widthFt is their type's section width)
      if (el.category === 'stair' || el.category === 'ramp' || el.category === 'beam') {
        const halfW = ((el.params.widthFt as number) || 3.5) / 2
        // side edges stay chord-based (curved runs are out of the CURVES round's scope
        // for these categories — their generators flatten anyway)
        const count = path.closed ? path.nodes.length : path.nodes.length - 1
        for (let i = 0; i < count; i++) {
          const a = path.nodes[i].pt, b = path.nodes[(i + 1) % path.nodes.length].pt
          const dx = b[0] - a[0], dy = b[1] - a[1]
          const len = Math.hypot(dx, dy)
          if (len < 1e-9) continue
          const nx = -dy / len, ny = dx / len
          for (const sgn of [1, -1]) {
            const ca: Pt2 = [a[0] + nx * sgn * halfW, a[1] + ny * sgn * halfW]
            const cb: Pt2 = [b[0] + nx * sgn * halfW, b[1] + ny * sgn * halfW]
            nodes.push({ pt: ca, elementId: el.id, nodeId: `${el.id}:e${i}${sgn}a` as NodeId })
            nodes.push({ pt: cb, elementId: el.id, nodeId: `${el.id}:e${i}${sgn}b` as NodeId })
            segments.push({ a: ca, b: cb, elementId: el.id })
          }
        }
      }
      continue
    }

    if (el.category === 'object' || el.category === 'casework') {
      // freestanding objects (OBJECTS round) and casework (FREESTANDING round,
      // 2026-08-19): every piece of the element's geometry rides the anchor, so ANY
      // exclusion of this element drops it all (an anchor drag would otherwise
      // self-snap to its own stale corners)
      if (exclude?.elementId === el.id) continue
      const anchor = objectAnchorPoint(el)
      const corners = objectFootprintCorners(el)
      if (!anchor || !corners) continue
      nodes.push({ pt: anchor, elementId: el.id, nodeId: `${el.id}:oa` as NodeId })
      corners.forEach((pt, k) => nodes.push({ pt, elementId: el.id, nodeId: `${el.id}:oc${k}` as NodeId }))
      for (let k = 0; k < 4; k++) segments.push({ a: corners[k], b: corners[(k + 1) % 4], elementId: el.id })
      continue
    }

    if (el.category === 'column') {
      // STRUCTURE round: center + profile ring — the plan promises "snaps at center +
      // corners/quadrants". Rect profiles contribute their 4 corners + 4 edges; round
      // profiles their 4 quadrant points (an edge on a tessellated circle would snap
      // to chord facets that aren't real geometry). Same whole-element exclusion rule
      // as objects: everything rides the anchor.
      if (exclude?.elementId === el.id) continue
      const anchor = objectAnchorPoint(el)
      const ring = columnProfileRing(el)
      if (!anchor || !ring) continue
      nodes.push({ pt: anchor, elementId: el.id, nodeId: `${el.id}:cc` as NodeId })
      if (ring.length === 4) {
        ring.forEach((pt, k) => nodes.push({ pt, elementId: el.id, nodeId: `${el.id}:ck${k}` as NodeId }))
        for (let k = 0; k < 4; k++) segments.push({ a: ring[k], b: ring[(k + 1) % 4], elementId: el.id })
      } else {
        const r = (((el.typeParams?.diameterFt ?? el.params.diameterFt) as number) || 0.375) / 2
        for (const [qx, qy] of [[r, 0], [0, r], [-r, 0], [0, -r]] as Pt2[]) {
          nodes.push({ pt: [anchor[0] + qx, anchor[1] + qy], elementId: el.id, nodeId: `${el.id}:cq${qx},${qy}` as NodeId })
        }
      }
      continue
    }

    if (HOSTED_CATS.has(el.category) && el.hostId) {
      const host = resolveElement(model, el.hostId)
      const corners = hostedFootprintCorners(host, el)
      if (!corners) continue
      const atCenter = hostedAnchorPoint(host!, el)
      corners.forEach((pt, k) => nodes.push({ pt, elementId: el.id, nodeId: `${el.id}:c${k}` as NodeId }))
      if (atCenter) nodes.push({ pt: atCenter, elementId: el.id, nodeId: `${el.id}:jc` as NodeId })
      for (let k = 0; k < 4; k++) segments.push({ a: corners[k], b: corners[(k + 1) % 4], elementId: el.id })
    }
  }
  // r23 structural GRIDS: model-level datums snap like geometry on EVERY level —
  // shared with the plan-substrate feed via collectGridSnapCandidates below.
  const g = collectGridSnapCandidates(model, exclude)
  nodes.push(...g.nodes)
  segments.push(...g.segments)
  // bim-plan/14 slice C: SITE records (property lines / envelopes / easements) snap
  // like grids do — model-level, every plan. Synthetic ids: a dimension witness on
  // a parcel corner degrades to its static point (the parcel never moves anyway).
  const st = collectSiteSnapCandidates(model, exclude)
  nodes.push(...st.nodes)
  segments.push(...st.segments)
  return { nodes, segments, arcs }
}

/**
 * Site snap candidates alone (bim-plan/14 slice C): property-line corners + edge
 * bodies, the derived setback ENVELOPE's corners + edges (dimensioning building-to-
 * setback is the site plan's daily measurement), and easement boundaries. All ids
 * synthetic (':'-carrying) — the structural-grid convention.
 */
export function collectSiteSnapCandidates(
  model: ModelDoc,
  exclude?: { elementId?: ElementId; nodeId?: NodeId },
): SnapCandidates {
  const nodes: SnapNode[] = []
  const segments: SnapSegment[] = []
  for (const rec of listSiteRecords(model)) {
    const rid = rec.id as unknown as ElementId
    if (exclude?.elementId === (rec.id as unknown as string)) continue
    const walk = (pts: Pt2[], closed: boolean, key: string) => {
      const n = pts.length
      for (let i = 0; i < n; i++) {
        nodes.push({ pt: [pts[i][0], pts[i][1]], elementId: rid, nodeId: `${rec.id}:${key}${i}` as NodeId })
        if (i < n - 1 || closed) {
          segments.push({ a: [pts[i][0], pts[i][1]], b: [pts[(i + 1) % n][0], pts[(i + 1) % n][1]], elementId: rid })
        }
      }
    }
    // stored corners exactly; edge bodies flattened (arc frontages snap on the chordal
    // tessellation — the underlay convention, close enough for site work)
    const flat = flattenSiteRing(rec)
    walk(flat.pts, rec.path.closed, 'p')
    if (rec.kind === 'propertyLine') {
      const env = siteEnvelope(rec)
      if (env) walk(env.nodes.map((nd) => nd.pt), true, 'e')
    }
  }
  return { nodes, segments }
}

/**
 * Structural-grid snap candidates alone (r23): endpoints + the line body, plus
 * grid×grid intersections (THE canonical column point). Synthetic nodeIds (they carry
 * ':') so dimension witnesses never capture a grid ref — a witness on a grid degrades
 * to its static point, the convention. Extracted from collectSnapCandidates (which
 * still folds it in) for the PLAN SNAP SUBSTRATE round: when a plan gesture's base
 * candidates come from the worker-delivered substrate, grids stay an ADDITIVE
 * kernel-side source merged on top — same rule, one implementation.
 */
export function collectGridSnapCandidates(
  model: ModelDoc,
  exclude?: { elementId?: ElementId; nodeId?: NodeId },
): SnapCandidates {
  const nodes: SnapNode[] = []
  const segments: SnapSegment[] = []
  const wholeExclude = exclude?.elementId && !exclude?.nodeId
  const grids = listGrids(model)
  const excludeId = exclude?.elementId as unknown as string | undefined
  for (const g of grids) {
    const gid = g.id as unknown as ElementId
    const isExcluded = excludeId === (g.id as unknown as string)
    if (isExcluded && wholeExclude) continue   // whole-grid drags never snap to themselves
    if (exclude?.nodeId !== `${g.id}:ga`) nodes.push({ pt: [g.a[0], g.a[1]], elementId: gid, nodeId: `${g.id}:ga` as NodeId })
    if (exclude?.nodeId !== `${g.id}:gb`) nodes.push({ pt: [g.b[0], g.b[1]], elementId: gid, nodeId: `${g.id}:gb` as NodeId })
    if (!isExcluded) segments.push({ a: [g.a[0], g.a[1]], b: [g.b[0], g.b[1]], elementId: gid })
  }
  for (let i = 0; i < grids.length; i++) {
    for (let j = i + 1; j < grids.length; j++) {
      const q = segmentIntersection(grids[i].a, grids[i].b, grids[j].a, grids[j].b)
      if (q) {
        nodes.push({
          pt: q,
          elementId: grids[i].id as unknown as ElementId,
          nodeId: `${grids[i].id}:x${grids[j].id}` as NodeId,
        })
      }
    }
  }
  return { nodes, segments }
}

/**
 * STORY UNDERLAY snap feed (docs/MODELER.md "STORY UNDERLAY + PLAN VIEW DEPTH"): the
 * same candidates `collectSnapCandidates` already exposes for `levelId`, but every
 * node's id is forced synthetic (':'-carrying) — an underlay is REFERENCE geometry
 * from another story, and a dimension witness must never capture a live ref into it
 * (it degrades to its static point instead, the structural-grid convention above).
 * Segments carry no nodeId and need no change.
 */
export function collectUnderlaySnapCandidates(model: ModelDoc, levelId: string): SnapCandidates {
  const { nodes, segments, arcs } = collectSnapCandidates(model, levelId)
  return {
    nodes: nodes.map((n) => ({ ...n, nodeId: `${n.nodeId}:underlay` as NodeId })),
    segments,
    ...(arcs ? { arcs } : {}),
  }
}

/**
 * WELD/JOIN CORNER snap candidates (SNAP AUDIT round, docs/MODELER.md 2026-08-14).
 * Where EXACTLY TWO wall ends coincide (the corner-weld topology — 3+/T stay butt,
 * the count-aware rule), the plan draws every matching-depth layer boundary EXTENDED
 * to its mutual intersection (plan.ts's P5e miter matcher). Those intersection points
 * were drawn but never snappable — this derives them as snap nodes.
 *
 * LAZY BY MANDATE (the r8 near-cursor-corridor rule): layer-edge candidates multiply,
 * so the work splits in two. `buildWallCornerIndex` resolves the level's wall ends
 * ONCE PER GESTURE (the same cadence candidates are gathered at — resolveAllElements
 * is not free); `cornerSnapNear` then runs PER POINTER MOVE and only corners whose
 * shared endpoint sits inside the corridor pay the O(layersA × layersB) intersection
 * math — never the whole level. Merged into gestures by `withCornerSnaps`
 * (snapLock.ts), never baked into collectSnapCandidates.
 *
 * Constants + matching rules mirror bim-geometry (plan.ts P5e + cornerWeld.ts), which
 * bim-kernel cannot import (check-boundaries.mjs) — keep in sync if either changes.
 * Node ids are synthetic (':'-carrying) so a dimension witness never captures a live
 * ref into derived join geometry (the structural-grid convention). v1 limit: corners
 * whose end segments are arcs are skipped (their miters follow circles, not lines).
 */
interface WallCornerEnd {
  id: ElementId; t: number; pt: Pt2
  /** unit direction from the endpoint INTO the wall */
  inward: Pt2
  /** left normal of TRAVEL at this end — the offset axis wallCoreLines uses */
  nTravel: Pt2
  sign: 1 | -1
  delta: number
  breaks: number[]
}
export interface WallCornerIndex {
  ends: WallCornerEnd[]
  /** EVERY wall end (excluded/arc ones too) — the exactly-2 count is real */
  presence: Pt2[]
}

export function buildWallCornerIndex(
  model: ModelDoc,
  levelId: string,
  exclude?: { elementId?: ElementId },
): WallCornerIndex {
  if (!profiling()) return buildWallCornerIndexImpl(model, levelId, exclude)
  const t0 = now()
  const res = buildWallCornerIndexImpl(model, levelId, exclude)
  perfMark('buildWallCornerIndex', now() - t0, { ends: res.ends.length })
  return res
}

function buildWallCornerIndexImpl(
  model: ModelDoc,
  levelId: string,
  exclude?: { elementId?: ElementId },
): WallCornerIndex {
  const ends: WallCornerEnd[] = []
  const presence: Pt2[] = []
  for (const el of resolveAllElements(model)) {
    if (el.levelId !== levelId || el.category !== 'wall') continue
    const path = el.params.path as Path2D | null
    if (!path?.nodes || path.nodes.length < 2 || path.closed) continue
    const t = el.assemblyThicknessFt
    if (!el.assembly || t <= 0) continue
    const endpoints = [path.nodes[0].pt, path.nodes[path.nodes.length - 1].pt]
    for (const pE of endpoints) presence.push([pE[0], pE[1]])
    if (exclude?.elementId === el.id) continue   // mid-drag geometry is stale — no miters off it
    const delta = centerlineOffsetFt(el.params.locationLine, el.params.flipped, el.assembly)
    const sign: 1 | -1 = el.params.flipped === true ? -1 : 1
    const breaks = [0]
    let cum = 0
    for (const layer of el.assembly.layers) { cum += layer.thicknessFt; breaks.push(cum) }
    for (const endIdx of [0, path.nodes.length - 1]) {
      const pE = path.nodes[endIdx].pt
      const endBulge = endIdx === 0 ? path.nodes[0].bulge : path.nodes[path.nodes.length - 2].bulge
      if (Math.abs(endBulge ?? 0) > 1e-12) continue   // v1: straight end segments only
      const pN = path.nodes[endIdx === 0 ? 1 : path.nodes.length - 2].pt
      const iv: Pt2 = [pN[0] - pE[0], pN[1] - pE[1]]
      const len = Math.hypot(iv[0], iv[1])
      if (len < 1e-9) continue
      const inward: Pt2 = [iv[0] / len, iv[1] / len]
      // travel runs pE→pN at node 0 (= inward) and pN→pE at the last node (= -inward)
      const tr: Pt2 = endIdx === 0 ? inward : [-inward[0], -inward[1]]
      ends.push({
        id: el.id, t, pt: [pE[0], pE[1]], inward, nTravel: [-tr[1], tr[0]], sign, delta, breaks,
      })
    }
  }
  return { ends, presence }
}

/** the per-move half: weld-corner nodes for corners inside the cursor's corridor */
export function cornerSnapNear(
  index: WallCornerIndex,
  nearPt: Pt2,
  corridorFt: number,
): SnapCandidates {
  if (!profiling()) return cornerSnapNearImpl(index, nearPt, corridorFt)
  const t0 = now()
  const res = cornerSnapNearImpl(index, nearPt, corridorFt)
  perfMark('cornerSnapNear', now() - t0, { ends: index.ends.length, welds: res.nodes.length })
  return res
}

function cornerSnapNearImpl(
  index: WallCornerIndex,
  nearPt: Pt2,
  corridorFt: number,
): SnapCandidates {
  const CORNER_TOL = 0.05    // cornerWeld.ts endpoint-coincidence tolerance
  const DEPTH_TOL = 0.015    // plan.ts P5e depth-alignment tolerance
  const MITER_LIMIT = 4      // cornerWeld.ts drafting bevel limit
  const nodes: SnapNode[] = []
  const eligible = index.ends.filter((w) =>
    Math.hypot(w.pt[0] - nearPt[0], w.pt[1] - nearPt[1]) <= corridorFt)
  if (eligible.length < 2) return { nodes, segments: [] }
  const { presence } = index

  const lineX = (p: Pt2, d: Pt2, q: Pt2, e: Pt2): Pt2 | null => {
    const cross = d[0] * e[1] - d[1] * e[0]
    if (Math.abs(cross) < 1e-6) return null
    const tt = ((q[0] - p[0]) * e[1] - (q[1] - p[1]) * e[0]) / cross
    return [p[0] + d[0] * tt, p[1] + d[1] * tt]
  }
  /** a boundary's end point at cumulative depth b (from the EXTERIOR face) */
  const boundaryEnd = (w: WallCornerEnd, b: number): Pt2 => {
    const off = w.delta + w.sign * (w.t / 2 - b)
    return [w.pt[0] + w.nTravel[0] * off, w.pt[1] + w.nTravel[1] * off]
  }
  const seen = new Set<string>()
  for (let i = 0; i < eligible.length; i++) {
    for (let j = i + 1; j < eligible.length; j++) {
      const A = eligible[i], B = eligible[j]
      if (A.id === B.id) continue   // hairpin
      if (Math.hypot(A.pt[0] - B.pt[0], A.pt[1] - B.pt[1]) > CORNER_TOL) continue
      // count-aware: EXACTLY two wall ends at this point (3+/T stay butt) — counted
      // against every wall end, including excluded/arc-ended ones this pass skipped
      let count = 0
      for (const p of presence) {
        if (Math.hypot(p[0] - A.pt[0], p[1] - A.pt[1]) <= CORNER_TOL) count++
      }
      if (count !== 2) continue
      // near-collinear continuation: faces butt, nothing miters (COLLINEAR_EPS)
      if (Math.abs(A.inward[0] * B.inward[1] - A.inward[1] * B.inward[0]) < 0.05) continue
      // reach = the outer-face miter tip distance; past MITER_LIMIT×t̄ the corner
      // BEVELS and its layer lines butt (cornerMiterReachFt's rule)
      const tBar = (A.t + B.t) / 2
      let tip = 0
      for (const fa of [0, A.t]) {
        for (const fb of [0, B.t]) {
          const X = lineX(boundaryEnd(A, fa), A.inward, boundaryEnd(B, fb), B.inward)
          if (X) tip = Math.max(tip, Math.hypot(X[0] - A.pt[0], X[1] - A.pt[1]))
        }
      }
      const beveled = tip > MITER_LIMIT * tBar
      const R = Math.max(tBar, beveled ? 0 : tip) + 0.75   // plan.ts P5e acceptance radius
      for (const dA of A.breaks) {
        for (const dB of B.breaks) {
          const aligned = Math.abs(dA - dB) < DEPTH_TOL ||
            Math.abs(dA - (B.t - dB)) < DEPTH_TOL ||
            Math.abs((A.t - dA) - dB) < DEPTH_TOL
          if (!aligned) continue
          const X = lineX(boundaryEnd(A, dA), A.inward, boundaryEnd(B, dB), B.inward)
          if (!X || Math.hypot(X[0] - A.pt[0], X[1] - A.pt[1]) > R * 1.5) continue
          const key = `${Math.round(X[0] * 1e4)},${Math.round(X[1] * 1e4)}`
          if (seen.has(key)) continue
          seen.add(key)
          nodes.push({ pt: X, elementId: A.id, nodeId: `${A.id}:weld:${key}` as NodeId })
        }
      }
    }
  }
  return { nodes, segments: [] }
}

/** the one-shot compose (tests + simple callers): index + near in one call */
export function collectCornerSnapCandidates(
  model: ModelDoc,
  levelId: string,
  nearPt: Pt2,
  corridorFt: number,
  exclude?: { elementId?: ElementId },
): SnapCandidates {
  return cornerSnapNear(buildWallCornerIndex(model, levelId, exclude), nearPt, corridorFt)
}

/** Proper segment×segment intersection (both finite), or null. */
function segmentIntersection(a1: Pt2, a2: Pt2, b1: Pt2, b2: Pt2): Pt2 | null {
  const d1: Pt2 = [a2[0] - a1[0], a2[1] - a1[1]]
  const d2: Pt2 = [b2[0] - b1[0], b2[1] - b1[1]]
  const cross = d1[0] * d2[1] - d1[1] * d2[0]
  if (Math.abs(cross) < 1e-12) return null
  const t = ((b1[0] - a1[0]) * d2[1] - (b1[1] - a1[1]) * d2[0]) / cross
  const u = ((b1[0] - a1[0]) * d1[1] - (b1[1] - a1[1]) * d1[0]) / cross
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null
  return [a1[0] + d1[0] * t, a1[1] + d1[1] * t]
}

/**
 * GENERAL LINE-INTERSECTION snap candidates (Matt, 2026-08-15 — "two walls not
 * officially joined, their location lines both exterior, no snap point where their
 * INTERIOR faces cross; all intersections should be candidates, not just wall-
 * endpoint-coincident weld corners"). ANY two on-screen segments that actually cross
 * become a snap node — no "joined" topology required, unlike `cornerSnapNear` (which
 * stays as-is for its own job: the wall-thickness-aware MITER/bevel math at exactly-2
 * coincident wall ends). This is the plain case: two lines cross somewhere, offer that
 * point.
 *
 * Runs off the CALLER's already-merged segment pool, which is the whole point — by the
 * time `withCornerSnaps` invokes this, `cands.segments` already holds element boundary/
 * layer lines (r6), hosted-opening footprints, grids, AND merged DXF/PDF/image underlay
 * segments (`snapCandidatesWithDxf`) — so underlay crossings snap for free, zero new
 * plumbing, exactly the "one shared stack" rule the SNAP AUDIT round set.
 *
 * LAZY BY THE r8 MANDATE, same shape as `cornerSnapNear`: filters to the handful of
 * segments actually near the cursor (a corridor, not the whole level's segment count)
 * before paying the O(k²) pairwise intersection math.
 */
/** GUIDE/SNAP HEALTH round: hard cap on the crossing scan's near set. The pairwise
 *  intersection math is O(k²); hovering a dense DXF cluster (hatch, text facets) put
 *  hundreds of segments in the corridor — tens of thousands of intersection tests per
 *  pointermove, the single biggest term in the measured 95 ms/move baseline. The k
 *  NEAREST segments keep every crossing the user can actually see at the cursor. */
const CROSSING_SCAN_CAP = 40

export function segmentCrossingsNear(
  segments: SnapSegment[],
  nearPt: Pt2,
  corridorFt: number,
): SnapNode[] {
  let near = segments.filter((s) => projectOntoSegment(nearPt, s.a, s.b).dist <= corridorFt)
  if (near.length > CROSSING_SCAN_CAP) {
    near = near
      .map((s) => ({ s, d: projectOntoSegment(nearPt, s.a, s.b).dist }))
      .sort((a, b) => a.d - b.d)
      .slice(0, CROSSING_SCAN_CAP)
      .map((e) => e.s)
  }
  const nodes: SnapNode[] = []
  const seen = new Set<string>()
  for (let i = 0; i < near.length; i++) {
    for (let j = i + 1; j < near.length; j++) {
      const A = near[i], B = near[j]
      if (A.elementId === B.elementId) continue   // one element's own edges meet at real nodes already
      const X = segmentIntersection(A.a, A.b, B.a, B.b)
      if (!X) continue
      const key = `${Math.round(X[0] * 1e4)},${Math.round(X[1] * 1e4)}`
      if (seen.has(key)) continue
      seen.add(key)
      nodes.push({ pt: X, elementId: A.elementId, nodeId: `${A.elementId}:x:${key}` as NodeId })
    }
  }
  return nodes
}

/**
 * FULL footprint corners for a hosted element (door/window/fixture), in world space —
 * door/window = the opening rectangle (jambs × wall faces); fixture = the mounted-face
 * plate rectangle. Casework moved to the freestanding footprint (`objectFootprintCorners`)
 * in the FREESTANDING round (2026-08-19) — it no longer has a host. Shared by
 * collectSnapCandidates (edge/corner snap targets, Matt r5) and the frontend's plan
 * selection-emphasis (a full-body highlight instead of a bare center dot, Matt r6 item A).
 * Placement mirrors bim-geometry's generate3d — keep in sync if that changes.
 */
export function hostedFootprintCorners(
  host: ResolvedElement | null | undefined,
  el: { category: string; params: Record<string, unknown>; typeParams?: Record<string, unknown> },
): Pt2[] | null {
  const hp = host?.params.path as Path2D | null | undefined
  if (!host || !hp?.nodes || hp.nodes.length < 2) return null
  // CURVES round: stations walk the FLATTENED host path (identical to the node points
  // for straight hosts) — the same list every other hostOffsetFt consumer measures
  const hostPts = flattenPathPts(hp)
  const s = (el.params.hostOffsetFt as number) || 0
  const w = (el.params.widthFt as number) || 3
  const t = host.assemblyThicknessFt || 0
  const delta = centerlineOffsetFt(host.params.locationLine, host.params.flipped, host.assembly)
  const atOff = (ss: number, off: number): Pt2 => {
    const { pt, dir } = polylinePointAt(hostPts, ss)
    return [pt[0] - dir[1] * off, pt[1] + dir[0] * off]   // left normal × off
  }
  // ELECTRICAL FIXTURES round: the fixture's plate footprint on its mounted FACE —
  // width/depth from the KIND (fixtureKinds.ts), side from `sideFlip`. Point-like at
  // wall scale, so the same chord-local station placement is correct on curved walls
  // too (the round's deliberate decision).
  if (el.category === 'fixture') {
    // LIGHT STYLES (2026-10-07): the kind lives on the TYPE, the style on the instance
    const merged = { ...(el.typeParams || {}), ...el.params }
    const dims = fixtureBodyDims(String(merged.fixtureKind || 'outlet'), merged)
    const inward = (host.params.flipped ? 1 : -1) * (el.params.sideFlip ? -1 : 1)
    const face = delta + inward * (t / 2)
    const f0 = s - dims.widthFt / 2, f1 = s + dims.widthFt / 2
    return [atOff(f0, face), atOff(f0, face + inward * dims.depthFt), atOff(f1, face + inward * dims.depthFt), atOff(f1, face)]
  }
  // AWNINGS (2026-10-08): the hood's plan footprint, out from the face it hangs on — the
  // resolved layout when there is one (fit to its opening); the pending params while placing
  if (el.category === 'awning') {
    const as = (el as { awningSpec?: AwningSpec }).awningSpec
    const sign = as ? as.sign : ((el.params.face === 'interior') !== (host.params.flipped === true) ? -1 : 1)
    const face = delta + sign * (t / 2)
    let f0: number, f1: number, u1: number
    if (as) {
      const fp = awningFootprintSU(as)
      f0 = fp.s0; f1 = fp.s1; u1 = fp.u1
    } else {
      const ww = (el.params.widthFt as number) || 6
      f0 = s - ww / 2; f1 = s + ww / 2
      u1 = ((el.params.projectionFt as number) || (el.typeParams?.projectionFt as number) || 32 / 12)
    }
    return [atOff(f0, face), atOff(f1, face), atOff(f1, face + sign * u1), atOff(f0, face + sign * u1)]
  }
  // WALL NICHE (2026-10-07): the recess on the face it opens to — rough width, rough
  // depth (the resolved spec when there is one; the pending params while placing)
  if (el.category === 'niche') {
    const ns = (el as { nicheSpec?: { roughWFt: number; roughDepthFt: number; sign: number } }).nicheSpec
    const rw = ns ? ns.roughWFt : ((el.params.widthFt as number) || 14.5 / 12) + 1 / 12
    const depth = Math.min(ns ? ns.roughDepthFt : ((el.params.depthFt as number) || 3.5 / 12) + 1 / 24, t)
    const sign = ns ? ns.sign : ((el.params.face === 'exterior') !== (host.params.flipped === true) ? 1 : -1)
    const outer = delta + sign * (t / 2), inner = delta + sign * (t / 2 - depth)
    const n0 = s - rw / 2, n1 = s + rw / 2
    return [atOff(n0, outer), atOff(n0, inner), atOff(n1, inner), atOff(n1, outer)]
  }
  const s0 = Math.max(0, s - w / 2), s1 = s + w / 2
  return [atOff(s0, delta - t / 2), atOff(s0, delta + t / 2), atOff(s1, delta + t / 2), atOff(s1, delta - t / 2)]
}

/** A freestanding object's stored anchor (back-center — objectKinds.ts frame), or null. */
export function objectAnchorPoint(
  el: { params: Record<string, unknown> },
): Pt2 | null {
  const path = el.params.path as Path2D | null | undefined
  const pt = path?.nodes?.[0]?.pt
  return pt ? [pt[0], pt[1]] : null
}

/**
 * A freestanding element's plan footprint corners in world space (OBJECTS round; also
 * casework post-FREESTANDING round, 2026-08-19): the width × depth rectangle in the
 * objectKinds.ts placement frame — anchor at back-center, local +y = front, rotated by
 * `rotationDeg`. Width/depth read INSTANCE-first, falling back to the TYPE — object has
 * neither as an instance param (always typeParams), casework has widthFt as a pure
 * instance param (TYPE/INSTANCE GOVERNANCE round) and depthFt on the type, so one read
 * order serves both correctly with no category branch. Shared by
 * collectSnapCandidates, the app's pick/marquee config, the selection wireframe, and
 * the translate ghost — one footprint, never re-derived per consumer.
 */
export function objectFootprintCorners(
  el: { category: string; params: Record<string, unknown>; typeParams?: Record<string, unknown> },
): Pt2[] | null {
  const anchor = objectAnchorPoint(el)
  if (!anchor) return null
  const w = (el.params?.widthFt as number) ?? (el.typeParams?.widthFt as number) ?? 2
  const d = (el.params?.depthFt as number) ?? (el.typeParams?.depthFt as number) ?? 2
  const rot = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
  const c = Math.cos(rot), s = Math.sin(rot)
  const at = (lx: number, ly: number): Pt2 =>
    [anchor[0] + lx * c - ly * s, anchor[1] + lx * s + ly * c]
  // LIGHT STYLES (2026-10-07): a point-placed fixture (ceiling or ground light) sits CENTRED on
  // its anchor — its symbol and its 3D both do — unlike an object, anchored at its back edge
  const y0 = el.category === 'fixture' ? -d / 2 : 0
  return [at(-w / 2, y0), at(w / 2, y0), at(w / 2, y0 + d), at(-w / 2, y0 + d)]
}

/** even-odd point-in-polygon (shared ray-cast — the spot-elevation/site-record
 *  convention, generalized 2026-08-24 for SURFACE HOSTING's rotated-rect
 *  footprint tests below; `pointInBoundary` is the Path2D-flattening wrapper). */
function pointInPolygon(pt: Pt2, pts: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j]
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}
function pointInBoundary(pt: Pt2, boundary: Path2D): boolean {
  return pointInPolygon(pt, flattenPathPts(boundary))
}

/**
 * CEILING HOSTING round (2026-08-23): which ceiling element on `levelId` covers
 * world point `pt` — the host a ceiling-mounted fixture (recessedCan/pendant/
 * flushMount) resolves onto at placement/drag/migration time. A level can have
 * more than one ceiling (different rooms, different assemblies); the FIRST
 * covering match wins (ceilings aren't expected to overlap on one level). Shared
 * by the migration (commands/elements.ts) and the app's placement/drag flow.
 */
export function ceilingAtPoint(model: ModelDoc, levelId: string | null | undefined, pt: Pt2): ResolvedElement | null {
  if (!levelId) return null
  for (const el of resolveAllElements(model)) {
    if (el.category !== 'ceiling' || el.levelId !== levelId) continue
    const boundary = el.params.boundary as Path2D | null | undefined
    if (!boundary?.nodes?.length) continue
    if (pointInBoundary(pt, boundary)) return el
  }
  return null
}

/**
 * SURFACE HOSTING round (2026-08-24, Matt: "casework, but also other surfaces
 * like slabs and other objects too, like if we had a pre manufactured
 * vanity"): which element under world point `pt` on `levelId` a freestanding
 * `object` placed there should auto-host onto — CASEWORK counters (a faucet on
 * a vanity cabinet) beat other OBJECTS (a faucet on a pre-manufactured vanity
 * object) beat the level's own FLOOR (an object hosted to a specific raised/
 * sunken floor slab follows ITS elevation, not the level's flat default) —
 * most-specific-surface-wins, mirroring the plan pick priority's own
 * opening > wall > slab ordering. `excludeId` skips an element hosting to
 * itself (re-host after a move). null = nothing under the point — the object
 * places UNHOSTED exactly like before this round; unlike ceiling fixtures,
 * hosting here is an ENHANCEMENT, never a requirement.
 */
export function surfaceHostAtPoint(
  model: ModelDoc, levelId: string | null | undefined, pt: Pt2, excludeId?: ElementId,
  /** the KIND being placed — anything but a counter-sitting kind (a sink) only ever
   *  hosts onto a FLOOR (SURFACE_SITTING_OBJECT_KINDS) */
  placingKind?: string,
): ResolvedElement | null {
  if (!levelId) return null
  const floorOnly = placingKind != null && !SURFACE_SITTING_OBJECT_KINDS.includes(placingKind)
  let caseworkHit: ResolvedElement | null = null
  let objectHit: ResolvedElement | null = null
  let floorHit: ResolvedElement | null = null
  for (const el of resolveAllElements(model)) {
    if (el.levelId !== levelId || el.id === excludeId) continue
    if (floorOnly && el.category !== 'floor') continue
    if (el.category === 'casework' && !caseworkHit) {
      const corners = objectFootprintCorners(el)
      if (corners && pointInPolygon(pt, corners)) caseworkHit = el
    } else if (el.category === 'object' && !objectHit && !RUG_KINDS.includes(String(el.typeParams?.objectKind || ''))) {
      const corners = objectFootprintCorners(el)
      if (corners && pointInPolygon(pt, corners)) objectHit = el
    } else if (el.category === 'floor' && !floorHit) {
      const boundary = el.params.boundary as Path2D | null | undefined
      if (boundary?.nodes?.length && pointInBoundary(pt, boundary)) floorHit = el
    }
    if (caseworkHit) break   // most-specific already found — no need to keep scanning
  }
  if (caseworkHit || objectHit || floorHit) return caseworkHit || objectHit || floorHit
  // PLANT HOSTING (2026-10-05): a plant over no slab or planter soil sits on the
  // terrain (on whatever level the terrain is modeled)
  // SITE UTILITIES (2026-10-07): the gas meter set and water meter box sit on it too
  if (placingKind && TERRAIN_HOSTED_OBJECT_KINDS.includes(placingKind)) {
    for (const el of resolveAllElements(model)) if (el.category === 'topo') return el
  }
  return null
}

/**
 * A column's plan PROFILE ring in world space (STRUCTURE round): rect = the width ×
 * depth rectangle centered on the anchor, rotated by `rotationDeg`; round = a
 * 20-segment circle of `diameterFt` (the tessellate-at-tolerance rule — columns are
 * small, a fixed count reads true). Shared by collectSnapCandidates, the geometry
 * generator's prism outline, the app's pick/marquee config, the selection wireframe,
 * and the placement/translate ghosts — one profile, never re-derived per consumer.
 * Type params win over instance-merged params so frontend ghosts can pass a bare
 * `{ params, typeParams }` stub before any element exists.
 */
export function columnProfileRing(
  el: { params: Record<string, unknown>; typeParams?: Record<string, unknown> },
): Pt2[] | null {
  const anchor = objectAnchorPoint(el)
  if (!anchor) return null
  const tv = (k: string): unknown => el.typeParams?.[k] ?? el.params[k]
  if (String(tv('profile') || 'rect') === 'round') {
    const r = ((tv('diameterFt') as number) || 0.375) / 2
    const pts: Pt2[] = []
    for (let i = 0; i < 20; i++) {
      const a = (Math.PI * 2 * i) / 20
      pts.push([anchor[0] + Math.cos(a) * r, anchor[1] + Math.sin(a) * r])
    }
    return pts
  }
  const w = (tv('widthFt') as number) || 3.5 / 12
  const d = (tv('depthFt') as number) || 3.5 / 12
  const rot = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
  const c = Math.cos(rot), s = Math.sin(rot)
  const at = (lx: number, ly: number): Pt2 =>
    [anchor[0] + lx * c - ly * s, anchor[1] + lx * s + ly * c]
  return [at(-w / 2, -d / 2), at(w / 2, -d / 2), at(w / 2, d / 2), at(-w / 2, d / 2)]
}

/** The hosted element's own station point on its host, at the host's centerline offset. */
function hostedAnchorPoint(host: ResolvedElement, el: { params: Record<string, unknown> }): Pt2 | null {
  const hp = host.params.path as Path2D | null | undefined
  if (!hp?.nodes || hp.nodes.length < 2) return null
  const hostPts = flattenPathPts(hp)
  const s = (el.params.hostOffsetFt as number) || 0
  const delta = centerlineOffsetFt(host.params.locationLine, host.params.flipped, host.assembly)
  const { pt, dir } = polylinePointAt(hostPts, s)
  return [pt[0] - dir[1] * delta, pt[1] + dir[0] * delta]
}

/** Convenience: candidates on the HOST's level for a hosted element's edits. */
export function snapLevelFor(model: ModelDoc, elementId: ElementId): string | null {
  const el = resolveElement(model, elementId)
  if (!el) return null
  if (el.hostId) {
    const host = resolveElement(model, el.hostId)
    return host ? host.levelId : el.levelId
  }
  return el.levelId
}

function projectOntoSegment(p: Pt2, a: Pt2, b: Pt2): { pt: Pt2; dist: number } {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  if (len2 < 1e-12) return { pt: [a[0], a[1]], dist: Math.hypot(p[0] - a[0], p[1] - a[1]) }
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  const q: Pt2 = [a[0] + dx * t, a[1] + dy * t]
  return { pt: q, dist: Math.hypot(p[0] - q[0], p[1] - q[1]) }
}

function orthoResolve(pt: Pt2, anchor: Pt2): Pt2 | null {
  const dx = pt[0] - anchor[0], dy = pt[1] - anchor[1]
  const len = Math.hypot(dx, dy)
  if (len < 1e-6) return null
  const ang = Math.atan2(dy, dx)
  const snapped = Math.round(ang / ORTHO_STEP) * ORTHO_STEP
  if (Math.abs(ang - snapped) > ORTHO_WITHIN) return null
  // MODEL RESOLUTION round: the length is the gesture's only free DOF — quantize it
  // (direction stays exact, so the point stays exactly on the 45°-increment line)
  const qlen = quantFt(len)
  return [anchor[0] + qlen * Math.cos(snapped), anchor[1] + qlen * Math.sin(snapped)]
}

/**
 * Perf instrumentation (GUIDE/SNAP PERF round, docs/MODELER.md 2026-08-14): a
 * zero-cost-when-off profiling wrapper around the hot per-pointermove functions,
 * kept in place (not stripped after the round) since it's the tool the NEXT report
 * on this path would reach for first. Enabled by setting `globalThis.__bimProfile
 * = true` in the console; read `globalThis.__bimPerfData` for aggregated
 * calls/ms/candidate-size per function.
 */
function perfMark(name: string, ms: number, extra: Record<string, number>): void {
  const g = globalThis as unknown as { __bimPerfData?: Record<string, any> }
  const store = (g.__bimPerfData ||= {})
  const e = (store[name] ||= { calls: 0, ms: 0 })
  e.calls++
  e.ms += ms
  for (const k in extra) e[k] = (e[k] || 0) + extra[k]
}
function profiling(): boolean {
  return typeof globalThis !== 'undefined' && !!(globalThis as unknown as { __bimProfile?: boolean }).__bimProfile
}
/** bim-kernel stays DOM-free (docs/MODELER.md "pure-TS") — no ambient `performance`. */
function now(): number {
  const perf = (globalThis as unknown as { performance?: { now(): number } }).performance
  return perf ? perf.now() : Date.now()
}

type ResolveSnapArgs = {
  pt: Pt2
  anchor?: Pt2 | null
  candidates?: SnapCandidates | null
  axes?: SnapAxis[] | null
  tolFt: number
  free?: boolean
  /** how many EQUAL divisions of each segment expose an interior snap point (Matt r4
   *  item 3): 2 = the midpoint (the shipped default), 3 = the two third-points, etc. */
  divisions?: number
  /** ORTHO round: explicit per-call override of the STANDING ortho mode (ortho.ts).
   *  Omitted everywhere in the app — every gesture inherits the mode, which is the
   *  point (see ortho.ts's note on why this isn't threaded through call sites). */
  ortho?: boolean
  /** PERPENDICULAR SNAPS round: the perpendicular rung's reference point when it
   *  differs from `anchor` — for gestures that want the PER osnap WITHOUT anchor's
   *  ortho/45° semantics (a dimension string's next pick measures perpendicular to
   *  a line from its previous witness, but must never be direction-constrained).
   *  Omitted = `anchor` is the reference, so every anchored gesture (wall draw,
   *  move base, grip origin) gets perpendicular for free. */
  perpAnchor?: Pt2 | null
}

/** how far PERPENDICULAR to the constrained axis an ortho candidate may sit and still
 *  project onto it — bounded (the house near-cursor doctrine) but reaching further
 *  than the 8× corridors, since aligning to something across the room IS the gesture.
 *  ×SNAP_PX(10) ≈ 240 screen px, so it feels the same at every zoom. */
const ORTHO_TRACK_MULT = 24

/**
 * Resolve a pointer position against candidates. `anchor` (the drag/draw origin)
 * enables ortho; `free` (Shift) disables ortho ONLY — node/edge snaps still apply
 * (matching the shipped Shift semantics, which never disabled anything but ortho).
 * Priority: node > midpoint > edge > axis > ortho. `axes` are directional constraint
 * lines (2.6 polish — extension/perpendicular snapping); a point within tolerance of an
 * axis projects onto it, which is what makes "drag along the line, type a distance"
 * land exactly on the intended line.
 */
export function resolveSnap(args: ResolveSnapArgs): SnapResult {
  // ORTHO round: the STANDING mode (ortho.ts) intercepts before anything else, so
  // every gesture family inherits it without a per-call-site flag. Shift INVERTS —
  // held, the resolve runs exactly as ortho-OFF-without-Shift would.
  if (args.ortho ?? orthoMode()) {
    if (!args.free) return resolveSnapOrtho(args)
    return resolveSnap({ ...args, ortho: false, free: false })
  }
  // GUIDE/SNAP PERF round: narrow the candidate pool to near the cursor before the
  // exact-math rungs below scan it — see snapIndex.ts. A safe superset, so results
  // are unchanged; only how much resolveSnapImpl has to look at changes.
  const near = args.candidates ? nearCandidates(args.candidates, args.pt, args.tolFt) : args.candidates
  const filtered = near === args.candidates ? args : { ...args, candidates: near }
  if (!profiling()) return resolveSnapImpl(filtered)
  const t0 = now()
  const res = resolveSnapImpl(filtered)
  perfMark('resolveSnap', now() - t0, {
    nodes: filtered.candidates?.nodes.length || 0,
    segments: filtered.candidates?.segments.length || 0,
    arcs: filtered.candidates?.arcs?.length || 0,
    axes: filtered.axes?.length || 0,
  })
  return res
}

/**
 * ORTHO resolution (docs/MODELER.md "ORTHO MODE + GUIDE DISCIPLINE"). The gesture is
 * constrained to the cardinal axis through `anchor` chosen by the dominant delta — but
 * snapping stays LIVE along it: every candidate PROJECTS onto the axis (the standard
 * CAD behavior Matt specified), so "run east until this lines up with that corner"
 * works. Priority mirrors the free rungs exactly: node > division point > edge, then
 * inference-guide crossings, then the bare constrained point.
 *
 * A candidate essentially ON the axis reports its REAL kind + target (byte-identical
 * meaning to an unconstrained snap). One reached by PROJECTION reports kind 'axis' with
 * TWO lines: the ortho constraint AND the alignment line through the source candidate
 * (perpendicular to the axis by construction — projection is orthogonal), so the
 * surfaces draw the classic tracking cross explaining WHY the motion stuck there
 * (ORTHO GUIDES round, 2026-08-17 — Matt: "snaps and temp guides don't appear to work
 * in ortho mode"; a bare stick with no visual read as broken).
 *
 * GUIDE rung (same round): the inference guides (`args.axes` — refGuides is live under
 * ortho now, its own discipline caps bound the storm) snap where they CROSS the
 * cardinal axis — ortho + guide tracking, the AutoCAD combination. A guide parallel to
 * the axis can't cross and never competes: ortho owns the direction.
 *
 * With no anchor (an origin pick, a bare hover) there is nothing to constrain: plain
 * snapping runs, guides included — only the anchored 45° rung stays off (ortho owns
 * direction the moment an anchor exists).
 */
function resolveSnapOrtho(args: ResolveSnapArgs): SnapResult {
  const { pt, anchor, candidates, tolFt } = args
  const divisions = Math.max(2, Math.round(args.divisions ?? 2))
  const c = anchor ? orthoConstrain(pt, anchor) : null
  if (!c) return resolveSnapImpl({ ...args, anchor: null })
  const { axis, proj } = c
  const [ux, uy] = axis.dir
  /** signed position ALONG the axis (the only coordinate motion is free in) */
  const along = (p: Pt2): number => (p[0] - axis.origin[0]) * ux + (p[1] - axis.origin[1]) * uy
  /** distance from the axis line — bounds how far a projection may reach */
  const perp = (p: Pt2): number => Math.abs((p[0] - axis.origin[0]) * uy - (p[1] - axis.origin[1]) * ux)
  const at = (s: number): Pt2 => [axis.origin[0] + ux * s, axis.origin[1] + uy * s]
  const s0 = along(proj)
  const trackFt = tolFt * ORTHO_TRACK_MULT
  const pool = candidates ? nearCandidates(candidates, proj, tolFt, trackFt) : candidates

  // GUIDE/SNAP HEALTH round: "on axis" means COINCIDENT, not merely snappable. The
  // old epsilon here was the full snap tolerance (tolFt — inches to a foot at working
  // zoom), so a node that far off the cardinal run was reported as a real kind:'node'
  // hit while the returned point was its PROJECTION onto the axis. The user saw the
  // node square, believed the corner closed, and committed a point up to tolFt away
  // from the actual node — no weld, near-coincident node pairs, and every later snap
  // in that area had two almost-identical references to fight over (a prime askew-
  // model bootstrap). Now: truly-coincident candidates return their EXACT point (the
  // model-resolution doctrine — exact coincidence outranks everything); off-axis ones
  // keep the honest 'axis' tracking result below.
  const coEps = Math.max(1e-4, tolFt * 1e-3)
  type Hit = { s: number; d: number; onAxis: boolean; kind: SnapResult['kind']; target?: SnapResult['target']; src: Pt2 }
  let bestNode: Hit | null = null
  let bestMid: Hit | null = null
  let bestEdge: Hit | null = null
  const offer = (
    cur: Hit | null, p: Pt2, kind: SnapResult['kind'], target?: SnapResult['target'],
  ): Hit | null => {
    const q = perp(p)
    if (q > trackFt) return cur
    const s = along(p)
    const d = Math.abs(s - s0)
    if (d > tolFt) return cur
    if (cur && cur.d <= d) return cur
    return { s, d, onAxis: q <= coEps, kind, target, src: p }
  }

  for (const n of pool?.nodes || []) {
    bestNode = offer(bestNode, n.pt, 'node', { elementId: n.elementId, nodeId: n.nodeId })
  }
  for (const s of pool?.segments || []) {
    for (let k = 1; k < divisions; k++) {
      const t = k / divisions
      const m: Pt2 = [s.a[0] + (s.b[0] - s.a[0]) * t, s.a[1] + (s.b[1] - s.a[1]) * t]
      bestMid = offer(bestMid, m, 'midpoint', { elementId: s.elementId })
    }
  }
  for (const a of pool?.arcs || []) {
    for (let k = 1; k < divisions; k++) {
      bestMid = offer(bestMid, arcPointAtAngle(a, a.a0 + (a.sweep * k) / divisions), 'midpoint', { elementId: a.elementId })
    }
  }
  // EDGE rung: the axis line CROSSING a segment/arc — the exact geometric point where
  // the constrained run meets that element (never a projection, so always on-axis)
  for (const s of pool?.segments || []) {
    const x = segmentAxisCross(s.a, s.b, axis.origin, axis.dir)
    if (x) bestEdge = offer(bestEdge, x, 'edge', { elementId: s.elementId })
  }
  for (const a of pool?.arcs || []) {
    for (const q of lineCircleIntersect(axis.origin, axis.dir, a.c, a.r)) {
      if (!angleOnArc(a, Math.atan2(q[1] - a.c[1], q[0] - a.c[0]), 1e-3)) continue
      bestEdge = offer(bestEdge, q, 'edge', { elementId: a.elementId })
    }
  }
  // GUIDE rung (ORTHO GUIDES round): an inference guide crossing the cardinal axis.
  // GUIDE/SNAP HEALTH round: the guide needs a REAL angle to the cardinal (≥ ~10°) —
  // a nearly-axis-parallel guide's crossing is ill-conditioned noise, same rule as
  // the free axis-intersection rung.
  let bestGuide: { s: number; d: number; ax: SnapAxis } | null = null
  for (const ax of args.axes || []) {
    const axLen = Math.hypot(ax.dir[0], ax.dir[1]) || 1
    const crossN = Math.abs((axis.dir[0] * ax.dir[1] - axis.dir[1] * ax.dir[0]) / axLen)
    if (crossN < 0.17) continue
    const x = lineLineCross(axis.origin, axis.dir, ax.origin, ax.dir)
    if (!x) continue
    const s = along(x)
    const d = Math.abs(s - s0)
    if (d > tolFt) continue
    if (!bestGuide || d < bestGuide.d) bestGuide = { s, d, ax }
  }

  // NEAREST-WINS MIDPOINTS: the same rule on a constrained axis
  const hit = (bestMid && bestNode && bestMid.d < bestNode.d * MID_BIAS ? bestMid : null) || bestNode || bestMid || bestEdge
  if (hit) {
    const p = at(hit.s)
    // truly coincident: return the candidate's EXACT point (≤ coEps off the axis —
    // invisible; exact node coincidence is what makes the corner weld form)
    if (hit.onAxis) return { pt: hit.src, kind: hit.kind, ...(hit.target ? { target: hit.target } : {}) }
    // tracking: surface the alignment line through the SOURCE candidate too — the
    // second axis is what the decoration draws to explain the stick
    const track = { origin: hit.src, dir: [-uy, ux] as Pt2 }
    return { pt: p, kind: 'axis', axes: [axis, track], ...(hit.target ? { target: hit.target } : {}) }
  }
  if (bestGuide) return { pt: at(bestGuide.s), kind: 'axis', axes: [axis, bestGuide.ax] }
  // MODEL RESOLUTION round: the along-axis coordinate is the only free DOF — quantize
  // it; the cross-axis coordinate is the anchor's own and stays bit-exact (a cardinal
  // dir from orthoConstrain is exactly [1,0]/[0,1], so this is component-wise)
  const qproj: Pt2 = ux !== 0 ? [quantFt(proj[0]), proj[1]] : [proj[0], quantFt(proj[1])]
  return { pt: qproj, kind: 'ortho' }
}

/** where two infinite lines cross (null when parallel) — the ortho guide rung's math */
function lineLineCross(o: Pt2, d: Pt2, o2: Pt2, d2: Pt2): Pt2 | null {
  const cross = d[0] * d2[1] - d[1] * d2[0]
  const norm = (Math.hypot(d[0], d[1]) || 1) * (Math.hypot(d2[0], d2[1]) || 1)
  if (Math.abs(cross / norm) < 1e-9) return null
  const t = ((o2[0] - o[0]) * d2[1] - (o2[1] - o[1]) * d2[0]) / cross
  return [o[0] + d[0] * t, o[1] + d[1] * t]
}

/** where an infinite axis line crosses a FINITE segment (null if parallel or beyond) */
function segmentAxisCross(a: Pt2, b: Pt2, origin: Pt2, dir: Pt2): Pt2 | null {
  const sdx = b[0] - a[0], sdy = b[1] - a[1]
  const cross = sdx * dir[1] - sdy * dir[0]
  if (Math.abs(cross) < 1e-12) return null
  const t = ((origin[0] - a[0]) * dir[1] - (origin[1] - a[1]) * dir[0]) / cross
  if (t < -1e-9 || t > 1 + 1e-9) return null
  return [a[0] + sdx * t, a[1] + sdy * t]
}

/** a midpoint beats a node in the aperture only when it is at most HALF the node's distance */
const MID_BIAS = 0.5

function resolveSnapImpl(args: ResolveSnapArgs): SnapResult {
  const { pt, anchor, candidates, axes, tolFt, free } = args
  const divisions = Math.max(2, Math.round(args.divisions ?? 2))

  if (candidates) {
    let bestNode: { n: SnapNode; d: number } | null = null
    for (const n of candidates.nodes) {
      const d = Math.hypot(pt[0] - n.pt[0], pt[1] - n.pt[1])
      if (d <= tolFt && (!bestNode || d < bestNode.d)) bestNode = { n, d }
    }
    // NEAREST-WINS MIDPOINTS (2026-10-01, Matt: "middle snaps aren't working … I want to
    // be able to snap to the middle of lines in detail components"): a node used to beat
    // a midpoint outright whenever both were inside the aperture, so on a SHORT edge (a
    // stud's 1-1/2" end, a washer) the midpoint could never be reached — its own ends sat
    // inside the aperture around it. Now the midpoint is computed first and wins when it
    // is clearly the nearer one (MID_BIAS keeps nodes a little sticky) — the AutoCAD /
    // Revit nearest-osnap rule. Long edges are unchanged: there the ends are never in range.
    const nodeHit = bestNode ? {
      pt: [bestNode.n.pt[0], bestNode.n.pt[1]] as Pt2,
      kind: 'node' as const,
      target: { elementId: bestNode.n.elementId, nodeId: bestNode.n.nodeId },
    } : null
    // segment DIVISION points snap like nodes (below real nodes, above the edge body).
    // `divisions` equal parts → interior points at k/divisions, k=1..divisions-1
    // (divisions=2 → the midpoint, the shipped default). CURVES round: arc edges
    // divide by equal ANGLE — the true arc midpoint, not the chord's (closed form,
    // constant work per arc — no sampling).
    let bestMid: { elementId: ElementId; pt: Pt2; d: number } | null = null
    for (const s of candidates.segments) {
      for (let k = 1; k < divisions; k++) {
        const t = k / divisions
        const m: Pt2 = [s.a[0] + (s.b[0] - s.a[0]) * t, s.a[1] + (s.b[1] - s.a[1]) * t]
        const d = Math.hypot(pt[0] - m[0], pt[1] - m[1])
        if (d <= tolFt && (!bestMid || d < bestMid.d)) bestMid = { elementId: s.elementId, pt: m, d }
      }
    }
    for (const a of candidates.arcs ?? []) {
      for (let k = 1; k < divisions; k++) {
        const m = arcPointAtAngle(a, a.a0 + (a.sweep * k) / divisions)
        const d = Math.hypot(pt[0] - m[0], pt[1] - m[1])
        if (d <= tolFt && (!bestMid || d < bestMid.d)) bestMid = { elementId: a.elementId, pt: m, d }
      }
    }
    if (bestMid && (!bestNode || bestMid.d < bestNode.d * MID_BIAS)) {
      return { pt: bestMid.pt, kind: 'midpoint', target: { elementId: bestMid.elementId } }
    }
    if (nodeHit) return nodeHit
    // PERPENDICULAR rung (PERPENDICULAR SNAPS round, docs/MODELER.md 2026-08-16): the
    // classic CAD PER osnap — the foot of the perpendicular from the gesture's
    // reference point (perpAnchor ?? anchor: the wall draw's last point, a move's
    // base, a dim string's previous witness) onto a candidate segment's span, or the
    // radial foot on an arc's swept range. Lives INSIDE the engine, not in a per-site
    // merge, so every gesture that has a reference — on every surface, now and in
    // future — offers it by construction (the plan-substrate round's structural
    // argument applied to a rung). Below node/midpoint (real geometry outranks a
    // derived foot), above the bare edge projection (near the foot, the exact
    // perpendicular wins over the cursor's own projection). Ortho mode never reaches
    // here with a constraint (resolveSnapOrtho owns direction) — by design.
    // ORTHO × EDGE (2026-09-29, Matt: "the perpendicular snap is screwing up our ability
    // to easily draw orthogonally … we end up with slightly askew walls"): the edge
    // and perpendicular rungs used to outrank the soft ortho rung, so a wall ended
    // wherever the cursor touched a face (a hair off vertical), or perpendicular to a
    // face that was itself a hair off square (a traced wall 0.56° off) — the skew
    // propagated. Now, when the cursor is inside the ortho cone from the anchor
    // (the same 45°-step / 7° reach as orthoResolve) and near a line, the point is
    // where the EXACT ortho ray crosses that line: on the face AND square. A true
    // perpendicular to an angled line (outside the cone) still wins as before.
    // the ortho reference: the gesture's anchor, else its perpendicular reference —
    // a wall END-GRIP drag passes only perpAnchor (the wall's fixed far end), and the
    // 2026-09-29 skew report was exactly that drag: without this the cone never
    // formed and the perpendicular to a hair-off face still won
    const orthoRef: Pt2 | null = anchor ?? args.perpAnchor ?? null
    const orthoDir: Pt2 | null = (() => {
      if (!orthoRef || free) return null
      const dx = pt[0] - orthoRef[0], dy = pt[1] - orthoRef[1]
      if (Math.hypot(dx, dy) < 1e-6) return null
      const ang = Math.atan2(dy, dx)
      const sn = Math.round(ang / ORTHO_STEP) * ORTHO_STEP
      if (Math.abs(ang - sn) > ORTHO_WITHIN) return null
      return [Math.cos(sn), Math.sin(sn)]
    })()
    const orthoTrackFt = tolFt * ORTHO_TRACK_MULT
    /** the ortho ray × segment crossing near the cursor, marked perpendicular only
     *  when the segment really is square to the ray (a true right angle) */
    const orthoOnSegment = (a: Pt2, b: Pt2, elementId: ElementId): SnapResult | null => {
      if (!orthoDir || !orthoRef) return null
      const q = segmentAxisCross(a, b, orthoRef, orthoDir)
      if (!q) return null
      if ((q[0] - orthoRef[0]) * orthoDir[0] + (q[1] - orthoRef[1]) * orthoDir[1] <= 1e-9) return null   // ahead of the reference
      if (Math.hypot(pt[0] - q[0], pt[1] - q[1]) > orthoTrackFt) return null
      const sdx = b[0] - a[0], sdy = b[1] - a[1], sl = Math.hypot(sdx, sdy) || 1
      const square = Math.abs((sdx * orthoDir[0] + sdy * orthoDir[1]) / sl) < 1e-6
      return {
        pt: q, kind: 'edge', target: { elementId },
        ...(square ? { perp: true, perpEdge: [[a[0], a[1]], [b[0], b[1]]] as [Pt2, Pt2] } : {}),
      }
    }
    const perpRef = args.perpAnchor ?? anchor
    if (perpRef && !(Math.abs(perpRef[0] - pt[0]) < 1e-12 && Math.abs(perpRef[1] - pt[1]) < 1e-12)) {
      // GUIDE/SNAP HEALTH round: the winner is no longer just "foot nearest the
      // cursor." Near-parallel lines stack in a real model — a wall's own assembly
      // faces (harmless: same perpendicular), but also the DXF line the wall was
      // TRACED over, offset and slightly rotated — and picking by bare foot distance
      // routinely landed a PER that was true to the underlay instead of the wall the
      // user was looking at (Matt's "rarely is that line actually perpendicular").
      // Rank lexicographically: live model geometry beats underlay tracings, then the
      // line whose BODY is nearest the cursor (the line you are visually on), then
      // the foot distance. The matched segment also rides out as `perpEdge` so the
      // surfaces can highlight WHICH line the perpendicular is measured against.
      let bestPerp: {
        pt: Pt2; d: number; elementId: ElementId; underlay: number; lineDist: number; edge?: [Pt2, Pt2]
      } | null = null
      const offer = (q: Pt2, elementId: ElementId, lineDist: number, edge?: [Pt2, Pt2]) => {
        if (Math.hypot(q[0] - perpRef[0], q[1] - perpRef[1]) < 1e-9) return   // ref sits on the line
        const d = Math.hypot(pt[0] - q[0], pt[1] - q[1])
        if (d > tolFt) return
        const underlay = String(elementId).startsWith('dxf:') ? 1 : 0
        const better = !bestPerp
          || underlay < bestPerp.underlay
          || (underlay === bestPerp.underlay && (lineDist < bestPerp.lineDist - 1e-9
            || (Math.abs(lineDist - bestPerp.lineDist) <= 1e-9 && d < bestPerp.d)))
        if (better) bestPerp = { pt: q, d, elementId, underlay, lineDist, edge }
      }
      for (const s of candidates.segments) {
        const dx = s.b[0] - s.a[0], dy = s.b[1] - s.a[1]
        const len2 = dx * dx + dy * dy
        if (len2 < 1e-12) continue
        const t = ((perpRef[0] - s.a[0]) * dx + (perpRef[1] - s.a[1]) * dy) / len2
        if (t < -1e-9 || t > 1 + 1e-9) continue   // foot must land ON the segment
        offer(
          [s.a[0] + dx * t, s.a[1] + dy * t], s.elementId,
          projectOntoSegment(pt, s.a, s.b).dist,
          [[s.a[0], s.a[1]], [s.b[0], s.b[1]]],
        )
      }
      for (const a of candidates.arcs ?? []) {
        const ux = perpRef[0] - a.c[0], uy = perpRef[1] - a.c[1]
        const L = Math.hypot(ux, uy)
        if (L < 1e-9) continue   // ref at center — every radial is "perpendicular"
        for (const sgn of [1, -1] as const) {
          const q: Pt2 = [a.c[0] + (sgn * a.r * ux) / L, a.c[1] + (sgn * a.r * uy) / L]
          if (!angleOnArc(a, Math.atan2(q[1] - a.c[1], q[0] - a.c[0]), 1e-3)) continue
          const body = projectToArc(a, pt)
          offer(q, a.elementId, body ? body.dist : Math.hypot(pt[0] - q[0], pt[1] - q[1]))
        }
      }
      if (bestPerp) {
        const hit = bestPerp as {
          pt: Pt2; d: number; elementId: ElementId; underlay: number; lineDist: number; edge?: [Pt2, Pt2]
        }
        // a perpendicular that is ALMOST ortho (the target line a hair off square)
        // takes the exact ortho crossing instead — the wall stays square
        if (orthoDir && hit.edge && perpRef === orthoRef) {
          const fx = hit.pt[0] - perpRef[0], fy = hit.pt[1] - perpRef[1], fl = Math.hypot(fx, fy)
          const off = fl > 1e-9 ? Math.acos(Math.min(1, Math.abs((fx * orthoDir[0] + fy * orthoDir[1]) / fl))) : 0
          if (off < (2 * Math.PI) / 180) {
            const o = orthoOnSegment(hit.edge[0], hit.edge[1], hit.elementId)
            if (o) return o
          }
        }
        return {
          pt: hit.pt, kind: 'edge', perp: true, target: { elementId: hit.elementId },
          ...(hit.edge ? { perpEdge: hit.edge } : {}),
        }
      }
    }
    let bestSeg: { s: SnapSegment; pt: Pt2; d: number } | null = null
    for (const s of candidates.segments) {
      const r = projectOntoSegment(pt, s.a, s.b)
      if (r.dist <= tolFt && (!bestSeg || r.dist < bestSeg.d)) bestSeg = { s, pt: r.pt, d: r.dist }
    }
    // arc BODY snap — radial projection onto the swept range (the SnapSegment loop's
    // arc analogue, not a special case bolted onto it)
    let bestArc: { a: SnapArc; pt: Pt2; d: number } | null = null
    for (const a of candidates.arcs ?? []) {
      const r = projectToArc(a, pt)
      if (r && r.dist <= tolFt && (!bestArc || r.dist < bestArc.d)) bestArc = { a, pt: r.pt, d: r.dist }
    }
    if (bestArc && (!bestSeg || bestArc.d < bestSeg.d)) {
      // guide × ARC intersection (the r8-family guide rung, closed form): when a
      // reference guide passes near the cursor while it rides the arc, snap to the
      // GUIDE × CIRCLE crossing instead of the bare radial projection
      if (axes && axes.length) {
        let bestX: { q: Pt2; ax: SnapAxis; dq: number } | null = null
        for (const ax of axes) {
          const alen = Math.hypot(ax.dir[0], ax.dir[1])
          if (alen < 1e-9) continue
          const ux = ax.dir[0] / alen, uy = ax.dir[1] / alen
          const along = (pt[0] - ax.origin[0]) * ux + (pt[1] - ax.origin[1]) * uy
          const gx = ax.origin[0] + ux * along, gy = ax.origin[1] + uy * along
          if (Math.hypot(pt[0] - gx, pt[1] - gy) > tolFt) continue   // guide must be near the cursor
          for (const q of lineCircleIntersect(ax.origin, ax.dir, bestArc.a.c, bestArc.a.r)) {
            if (!angleOnArc(bestArc.a, Math.atan2(q[1] - bestArc.a.c[1], q[0] - bestArc.a.c[0]), 1e-3)) continue
            const dq = Math.hypot(pt[0] - q[0], pt[1] - q[1])
            // GUIDE/SNAP HEALTH round: 1.5× reach (was 2×) — normalized with the
            // axis-intersection rung so no guide rung out-reaches its peers
            if (dq <= tolFt * 1.5 && (!bestX || dq < bestX.dq)) bestX = { q, ax, dq }
          }
        }
        if (bestX) {
          return { pt: bestX.q, kind: 'axis', axes: [bestX.ax], target: { elementId: bestArc.a.elementId } }
        }
      }
      return { pt: bestArc.pt, kind: 'edge', target: { elementId: bestArc.a.elementId } }
    }
    if (bestSeg) {
      // guide × edge intersection (polish round 6 — Matt's report: sliding along a
      // line, guides never fired because the edge rung outranked them): when a
      // reference guide passes near the cursor while it rides a segment, snap to the
      // SEGMENT-LINE × GUIDE intersection instead of the bare edge projection.
      if (axes && axes.length) {
        const sa = bestSeg.s.a, sb = bestSeg.s.b
        const sdx = sb[0] - sa[0], sdy = sb[1] - sa[1]
        const slen2 = sdx * sdx + sdy * sdy
        let bestX: { q: Pt2; ax: SnapAxis; dq: number } | null = null
        for (const ax of axes) {
          const alen = Math.hypot(ax.dir[0], ax.dir[1])
          if (alen < 1e-9 || slen2 < 1e-18) continue
          // the guide itself must be near the cursor
          const ux = ax.dir[0] / alen, uy = ax.dir[1] / alen
          const along = (pt[0] - ax.origin[0]) * ux + (pt[1] - ax.origin[1]) * uy
          const gx = ax.origin[0] + ux * along, gy = ax.origin[1] + uy * along
          if (Math.hypot(pt[0] - gx, pt[1] - gy) > tolFt) continue
          const cross = sdx * ax.dir[1] - sdy * ax.dir[0]
          if (Math.abs(cross / (Math.sqrt(slen2) * alen)) < 1e-6) continue
          const t = ((ax.origin[0] - sa[0]) * ax.dir[1] - (ax.origin[1] - sa[1]) * ax.dir[0]) / cross
          const q: Pt2 = [sa[0] + sdx * t, sa[1] + sdy * t]
          // GUIDE/SNAP HEALTH round: the intersection must land ON the segment —
          // the old ±10% slop happily snapped to a "crossing" past the line's end
          if (t < -1e-9 || t > 1 + 1e-9) continue
          const dq = Math.hypot(pt[0] - q[0], pt[1] - q[1])
          // 1.5× reach (was 2×) — normalized with the axis-intersection rung
          if (dq <= tolFt * 1.5 && (!bestX || dq < bestX.dq)) bestX = { q, ax, dq }
        }
        if (bestX) {
          return { pt: bestX.q, kind: 'axis', axes: [bestX.ax], target: { elementId: bestSeg.s.elementId } }
        }
      }
      // ORTHO × EDGE: near a line inside the ortho cone, land where the exact ortho
      // ray crosses it (not wherever the cursor happens to touch it)
      const o = orthoOnSegment(bestSeg.s.a, bestSeg.s.b, bestSeg.s.elementId)
      if (o) return o
      return { pt: bestSeg.pt, kind: 'edge', target: { elementId: bestSeg.s.elementId } }
    }
  }

  if (axes && axes.length) {
    const near: { ax: SnapAxis; pt: Pt2; d: number }[] = []
    for (const ax of axes) {
      const len = Math.hypot(ax.dir[0], ax.dir[1])
      if (len < 1e-9) continue
      const ux = ax.dir[0] / len, uy = ax.dir[1] / len
      const t = (pt[0] - ax.origin[0]) * ux + (pt[1] - ax.origin[1]) * uy
      const q: Pt2 = [ax.origin[0] + ux * t, ax.origin[1] + uy * t]
      const d = Math.hypot(pt[0] - q[0], pt[1] - q[1])
      if (d <= tolFt) near.push({ ax, pt: q, d })
    }
    near.sort((a, b) => a.d - b.d)
    // two guides crossing near the cursor snap to their INTERSECTION (the ArchiCAD
    // guide-line move) — non-parallel pair, intersection within a slightly wider reach.
    // GUIDE/SNAP HEALTH round: the pair needs a REAL angle between them (≥ ~10°, the
    // sin below). The old 1e-6 floor accepted two guides half a degree apart — a
    // numerically wild intersection that can land anywhere along the pair, and when it
    // happened to fall inside the reach it snapped geometry to a garbage point drawn
    // as a confident two-guide crossing (one of the askew-model bootstrap paths).
    const MIN_CROSS_ANGLE_SIN = 0.17
    for (let i = 0; i < near.length && i < 4; i++) {
      for (let j = i + 1; j < near.length && j < 4; j++) {
        const a = near[i].ax, b = near[j].ax
        const cross = a.dir[0] * b.dir[1] - a.dir[1] * b.dir[0]
        const la = Math.hypot(a.dir[0], a.dir[1]), lb = Math.hypot(b.dir[0], b.dir[1])
        if (Math.abs(cross / (la * lb)) < MIN_CROSS_ANGLE_SIN) continue
        const t = ((b.origin[0] - a.origin[0]) * b.dir[1] - (b.origin[1] - a.origin[1]) * b.dir[0]) / cross
        const q: Pt2 = [a.origin[0] + a.dir[0] * t, a.origin[1] + a.dir[1] * t]
        if (Math.hypot(pt[0] - q[0], pt[1] - q[1]) <= tolFt * 1.5) {
          return { pt: q, kind: 'axis', axes: [a, b] }
        }
      }
    }
    if (near.length) return { pt: near[0].pt, kind: 'axis', axes: [near[0].ax] }
  }

  if (anchor && !free) {
    const o = orthoResolve(pt, anchor)
    if (o) return { pt: o, kind: 'ortho' }
  }
  // MODEL RESOLUTION round: a raw click has no coincidence intent — both coordinates
  // land on the 1/64" grid, so free input can never introduce sub-tolerance noise
  return { pt: quantPt(pt), kind: 'none' }
}
